/** Every string of the mcper.io app. Short, generic, no site or person names. */
export const t = {
  name: 'MCPer.io',
  nav: {
    overview: 'Overview',
    sites: 'Sites',
    signIns: 'Sign-ins',
    agents: 'Agents',
    calls: 'Calls',
    settings: 'Settings',
    signOut: 'Sign out',
    menu: 'Menu',
    library: 'Library',
    sitesCount: (n: number) => `${n} site${n === 1 ? '' : 's'}`,
    buildingCount: (n: number) => `Building now: ${n}`,
  },
  login: {
    title: 'Sign in to get your URL',
    subtitle: 'One address your agent uses for every site. Sign-ins to sites stay yours.',
    continueWith: (provider: string) => `Continue with ${provider}`,
    providerName: { google: 'Google', microsoft: 'Microsoft', oidc: 'your account' } as Record<string, string>,
    emailLabel: 'Email',
    emailPlaceholder: 'you@example.com',
    localButton: 'Sign in',
    localHint: 'Development sign-in: no password. Production uses the buttons above.',
    loading: 'Loading…',
    anyAccount: 'Any account works. New accounts are created on first sign-in.',
    waiting: (agent: string) => `${agent} is waiting for you to sign in.`,
    home: 'mcper.io',
  },
  overview: {
    title: 'Overview',
    subtitle: 'Your agents reach the web through one address.',
    copy: 'Copy',
    copied: 'Copied',
    setupGuide: 'Setup guide',
    cards: {
      sites: 'Sites you use',
      sitesNone: 'None yet',
      sitesDetail: (library: number, built: number) => `${library} from the library, ${built} built by you`,
      sitesDetailOne: 'From the library',
      calls: 'Calls, 7 days',
      callsNone: 'No calls yet',
      callsDetail: (writes: number) => `${writes} write${writes === 1 ? '' : 's'}, all confirmed by you`,
      answer: 'Typical answer',
      answerNone: 'No calls yet',
      answerDetail: (api: string, replay: string) => `API calls ${api}, replays ${replay}`,
      plan: 'Plan',
      planFree: 'Free',
      planDetail: (used: number, cap: number) => `${used} of ${cap} calls this month`,
    },
    growth: {
      title: 'mcper.io growth',
      onlyYou: 'Only you see this',
      library: 'Sites in the library',
      libraryDetail: (building: number) => `${building} being added now`,
      people: 'People',
      peopleDetail: 'Signed up so far',
      active: 'Active, 7 days',
      activeDetail: 'People whose agent ran a call',
      calls: 'Calls, 7 days',
      callsDetail: 'Across everyone',
    },
    sites: 'Sites you have used',
    browseLibrary: 'Browse the library',
    sitesEmpty: 'No sites yet. Ask your agent for one and it appears here.',
    building: 'Building now',
    buildingEmpty: 'Nothing is being built.',
    buildingEta: (seconds: number) => `first build · about ${seconds} s left`,
    buildingProgress: (pct: number) => `first build · ${pct}%`,
    buildingWait: 'Your agent was told to wait.',
    signIns: 'Sign-ins',
    signInsEmpty: 'No sign-ins yet. A site that needs one asks you through your agent.',
    signInsNote: 'Stored encrypted, yours only. Revoke any of them here.',
    agents: 'Agents',
    addAgent: 'Add an agent',
    agentsEmpty: 'No agent has connected yet.',
    agentActivity: (calls: number, when: string) => `${calls} call${calls === 1 ? '' : 's'} · ${when}`,
    recentCalls: 'Recent calls',
    allCalls: 'All calls',
    callsEmpty: 'No calls yet.',
  },
  setup: {
    title: 'Setup guide',
    subtitle: 'Add one address to your agent. Everything else happens through it.',
    url: 'Your URL',
    claudeCode: 'Claude Code',
    claudeAi: 'claude.ai',
    claudeAiSteps: 'Settings → Connectors → Add custom connector → paste the URL.',
    cursor: 'Cursor and other MCP clients',
    cursorHint: 'Add it as an HTTP MCP server in the client settings.',
    then: 'Then ask your agent for any site. First time on a site, it waits a minute or two while the site is built.',
    back: 'Back to overview',
  },
  table: { site: 'Site', tools: 'Tools', kind: 'Kind', signIn: 'Sign-in', lastCall: 'Last call', lastUsed: 'Last asked', status: 'Status', expires: 'Expires', agent: 'Agent', lastActive: 'Last active', call: 'Call', when: 'When', took: 'Took' },
  status: { ready: 'Ready', building: 'Building', unbuildable: 'Not buildable', failed: 'Retrying' } as Record<'ready' | 'building' | 'unbuildable' | 'failed', string>,
  signInKind: { none: 'none needed', token: 'personal token' } as Record<'none' | 'token', string>,
  ms: (n?: number) => (n === undefined ? '—' : n < 1000 ? `${n} ms` : `${(n / 1000).toFixed(1)} s`),
  time: { now: 'just now', minutes: (n: number) => `${n} min ago`, hours: (n: number) => `${n} h ago`, yesterday: 'yesterday', days: (n: number) => `${n} days ago` },
  sites: { title: 'Sites', subtitle: 'Sites you have used, and the library everyone shares.', empty: 'No sites yet. Ask your agent for one and it appears here.', yours: 'Yours', library: 'Library', libraryEmpty: 'The library is empty for now.', search: 'Search the library', searchPlaceholder: 'Site name' },
  signIns: {
    title: 'Sign-ins',
    subtitle: 'Your sign-ins to sites. Encrypted, yours only.',
    empty: 'No sign-ins yet. A site that needs one asks you through your agent.',
    revoke: 'Revoke',
    revoking: 'Revoking…',
    replace: 'Replace',
    tokenOk: 'token · ok',
    expiredSignIn: 'expired · sign in',
    state: { connected: 'Signed in', expired: 'Expired', revoked: 'Revoked', never: 'Not signed in' } as Record<string, string>,
    since: 'Since',
    lastUsed: 'Last used',
    never: 'not yet',
  },
  signInSite: {
    title: (name: string) => `Sign in to ${name}`,
    subtitle: 'Paste your token once. It is stored encrypted, for you only. Your agent never sees it.',
    label: (site: string) => `Your token for ${site}`,
    hint: 'Most sites show it in their settings, under API, Developer or Integrations.',
    save: 'Save and continue',
    saving: 'Saving…',
    done: (site: string) => `Signed in to ${site}. Go back to your agent and ask again.`,
    already: 'You are signed in to this site. Paste a new token to replace it.',
    noneNeeded: 'This site needs no sign-in. Your agent can use it as it is.',
    notFound: 'This site is not in the library yet. Ask your agent for it first.',
    dashboard: 'Open your dashboard',
  },
  agents: {
    title: 'Agents',
    subtitle: 'Agents that can use your address. Revoke one and it stops at once.',
    empty: 'No agent has connected yet. Add your address to one from the setup guide.',
    add: 'Add an agent',
    revoke: 'Revoke',
    revoking: 'Revoking…',
    connected: 'Connected',
    lastActive: 'Last active',
    calls: 'Calls',
    never: 'not yet',
  },
  calls: {
    title: 'Calls',
    subtitle: 'What ran, where, and how it went. Input values are never kept.',
    empty: 'No calls yet.',
    kind: { read: 'read', preview: 'preview', write: 'write', refused: 'refused' } as Record<string, string>,
    ok: 'ok',
    failed: 'failed',
    /** A failed call in words, by its error code. */
    why: (code?: string): string =>
      ({ tl05: 'sign in again', tl04: 'site error', tl07: 'timed out', tl02: 'wrong input', tl03: 'not confirmed', tl01: 'no such tool', wk03: 'busy', wk04: 'interrupted' })[code ?? ''] ?? 'failed',
  },
  settings: {
    title: 'Settings',
    subtitle: 'Your plan and your account.',
    plan: 'Plan',
    planFree: 'Free',
    usage: (calls: number, cap: number, renews: string) => `${calls} of ${cap} calls used this month. Renews ${renews}.`,
    planNote: 'Previews are free; a call counts when it runs.',
    account: 'Account',
    signedInAs: (email: string) => `Signed in as ${email}`,
    signOut: 'Sign out',
    deleteTitle: 'Delete your account',
    deleteBody: 'Removes your sign-ins to sites, your agents, your call history and your account. Sites in the library stay for everyone.',
    deleteButton: 'Delete my account',
    deleteConfirm: 'This cannot be undone. Delete for good?',
    deleteYes: 'Delete for good',
    deleteNo: 'Keep my account',
    deleting: 'Deleting…',
  },
  authorize: {
    title: (agent: string) => `${agent} wants your MCPer.io address`,
    body: 'Once you allow it, it can:',
    points: ['ask for any site and get its tools', 'call those tools as you, with a preview before anything changes', 'browse the library'],
    note: 'It never sees your sign-ins to sites. You can revoke it any time under Agents.',
    allow: 'Allow',
    deny: 'Cancel',
    working: 'Sending you back to your agent…',
    notAllowed: 'You cannot allow this.',
    expired: 'This request has expired. Start again from your agent.',
  },
};
